<template lang="pug">
	.schedule-event-rule.rule.flex-row.space.justify.align-center.wrap
		.left.flex-row.space.align-center
			.flex-col.center
				DxButton(v-if="rule", :activeStateEnabled="false", icon="trash", type="danger", @click="v => $emit('remove', rule)", stylingMode="text", :disabled="isLoading")
				DxButton(v-else, :activeStateEnabled="false", icon="edit", type="warning", @click="() => editRule()", stylingMode="text", :disabled="isLoading")
			i.grey.fas(v-if="icon", :class="[icon]")
			.flex-col
				.font-small.upper.grey(v-if="rule") {{$tCaption('Name')}}
				span(v-if="!rule") {{name}}
				DxTextBox(v-else, :width="200", :value.sync="rule.name", :disabled="isLoading", valueChangeEvent="input")
			template(v-if="!rule && isEditing")
				.px-loader.loading.flex-row.space
					.loader
						i.fas.fa-circle-notch.fa-spin
					span {{$tCaption('Loading') + '..'}}
			template(v-if="rule")
				template(v-if="isDatasetRule")
					.flex-col
						.font-small.upper.grey {{$tCaption('Dataset')}}
						Dropdown(:getTotal="(d) => d.TotalNumberOfRecords", :getItems="(d) => d.Results", :getData="$Data.getDynamicDatasets",
							:columns="[{ dataField: 'Name', caption: $tCaption('Dataset') }]",
							displayExpr="Name",
							valueExpr="Oid",
							:placeholder="$tCaption('Dataset')",
							v-model="rule.dataSet",
							:showColumnHeaders="false",
							:filtersVisible="false",
							:searchVisible="false",
							:multiple="false")
				template(v-else)
					.flex-col
						.font-small.upper.grey {{$tCaption('External service')}}
						Dropdown(:getTotal="(d) => d.TotalNumberOfRecords", :getItems="(d) => d.Results", :getData="$Data.getExternalServices",
							:columns="[{ dataField: 'Name', caption: $tCaption('External service') }]",
							displayExpr="Name",
							valueExpr="Oid",
							:placeholder="$tCaption('External service')",
							v-model="rule.externalServiceId",
							:showColumnHeaders="false",
							:filtersVisible="false",
							:searchVisible="true",
							:multiple="false")
				.flex-col
					.font-small.upper.grey {{$tCaption('Criteria')}}
					.flex-row.space
						DxButton(icon="filter", :text="$tCaption('Set criteria')", @click="v => setCriteria()", :disabled="!(rule.dataSet || rule.externalServiceId) || isLoading")
						DxButton(v-if="hasChanges", :text="$tCaption('Save', '@actions')", :disabled="!hasChanges || isSaving", @click="save()", type="success")
		.right.flex-row.space.align-center
			DxButton(:text="$tCaption('Evaluate rule')", :activeStateEnabled="false", icon="fas fa-play", type="default", stylingMode="text", @click="runEventRule", :disabled="isEvaluating")
</template>

<script>
	import Data from 'app-core/Data';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import FileUploader from 'app-core/FileUploader.vue';
	import Popup from 'app-core/Popup.vue';
	import DxFilterBuilder from 'devextreme-vue/filter-builder';
	import Dropdown from 'app-core/Dropdown.vue';

	export default {
		translationCategory: 'schedule.ScheduleEventRule',
		name: 'schedule-event-rule',
		components: {
			DxTextBox, DxSelectBox, DxButton, FileUploader, Popup, DxFilterBuilder, Dropdown
		},
		model: {
			prop: 'rule',
			event: 'change'
		},
		props: ['ruleId', 'name', 'edit', 'icon', 'newRule', 'isDatasetRule'],
		data() {
			return {
				rule: null,
				isEditing: !!this.edit,
				isLoading: false,
				hasChanges: false,
				isSaving: false,
				isEvaluating: false
			};
		},
		computed: {
			ruleIsSpecified() {
				return !!this.rule;
			}
		},
		watch: {
			'rule.name'() { this.change(); },
			'rule.dataSet'() { this.change(); },
			'rule.externalServiceId'() { this.change(); },
			'rule.runParameters':{
				handler() {
					this.change();
				},
				deep: true
			},
			'rule.criteria'() { this.change(); },
			'ruleId'() {
				this.isEditing = !!this.edit;
				this.rule = this.newRule;
				if (this.isEditing)
					this.loadRule();
			}
		},
		mounted() {
			if (this.isEditing)
				this.loadRule();
		},
		methods: {
			save(callback, silent) {
				if (this.isEditing) {
					this.isSaving = true;
					this.$emit('change', this.rule, () => {
						this.isSaving = false;
						if (callback)
							callback();

						if (!silent)
							this.$notifySuccess(this.$tCaption('Data saved'));
					});
				} else {
					if (callback)
						callback();
				}
			},
			change() {
				if (this.ruleIsSpecified) {
					this.hasChanges = true;
				}
			},
			loadRule() {
				if (this.ruleId && this.isEditing) {
					this.isLoading = true;
					const method = this.isDatasetRule ? 'GetDynamicDatasetRule' : 'GetExternalServiceRule';
					return Data.getExternalApi(`EventRule/${method}`, `eventRuleId=${this.ruleId}`).then(r => {
						if (__DEBUG__)
							console.log('Rule loaded', { r });
						if (!r)	return;
						var runParameters = {};
						(r.RunParameters || []).map(p => {
							runParameters[p.Name] = p.Value;
						});
						this.rule = {
							id: r.Oid,
							name: r.Name,
							dataSet: r.DynamicDatasetId,
							externalServiceId: r.ExternalServiceId,
							criteria: JSON.parse(r.JsonCriteria),
							runParameters: runParameters,
							type: r.Type
						};
					}).finally(() => this.isLoading = false);
				}
				else
					this.rule = this.newRule;
			},
			editRule() {
				this.isEditing = true;
				this.loadRule();
			},
			setCriteria() {
				if (this.isDatasetRule)
					this.$emit('set-criteria', this.rule);
				else {
					this.$Data.getExternalServices(0, 1, { byKey: this.rule.externalServiceId }).then(r => {
						this.$emit('set-criteria', this.rule, r.Results[0]);
					});
				}
			},
			runEventRule() {
				this.isEvaluating = true;
				this.save(() => {
					return Data.getExternalApi('EventRule/RunEventRule', `eventRuleId=${this.ruleId}`).then(r => {
						if (r.Success) {
							this.$notifySuccess(`${this.$tCaption('Result')}: ${JSON.stringify(r.Data, null, '\t')}`);
						} else {
							this.$notifyError(`${this.$tCaption('Error')}: ${JSON.stringify(r.Data, null, '\t')}`);
						}
					})
					.catch(ex => this.$notifyError(ex))
					.finally(() => this.isEvaluating = false);
				}, true);
			}
		}
	};
</script>
